import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../../supabaseClient";
import ShieldImg from "../../Assets/Icons/shield.png";
import LoaderBox from "../HomePage/LoaderBox";

const AdminStartMatch = () => {
  const [activeMatch, setActiveMatch] = useState(null);
  const [scheduledMatches, setScheduledMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingEvent, setEditingEvent] = useState(null);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [showRosterAdmin, setShowRosterAdmin] = useState(null);
  const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
  const [playerModalTeam, setPlayerModalTeam] = useState(null);
  const anyMatchLive = scheduledMatches.some((m) => m.is_live);

  // 1. Učitavanje utakmica i provjera je li neka već LIVE
  const loadMatches = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("matches")
      .select(`*, team_a:team_a_id (*), team_b:team_b_id (*)`)
      .eq("is_completed", false);

    if (data) {
      setScheduledMatches(
        data.map((m) => ({
          ...m,
          display_name_a: m.team_a?.name || "Tim A",
          display_name_b: m.team_b?.name || "Tim B",
          logo_a: m.team_a?.logo_url || ShieldImg,
          logo_b: m.team_b?.logo_url || ShieldImg,
          scorea: m.scorea || 0,
          scoreb: m.scoreb || 0,
          events: m.events || [],
          rostera: m.rostera?.length > 0 ? m.rostera : m.team_a?.players || [],
          rosterb: m.rosterb?.length > 0 ? m.rosterb : m.team_b?.players || [],
          is_live: m.is_live || false,
          half: m.half || "1. poluvrijeme",
        })),
      );
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadMatches();
  }, [loadMatches]);

  // Sinkronizacija s bazom podataka
  const syncWithDB = async (updates) => {
    if (!activeMatch) return;
    await supabase.from("matches").update(updates).eq("id", activeMatch.id);
  };

  // Dodavanje novog igrača u TIM i MEČ
  const addNewPlayer = (teamLetter) => {
    setPlayerModalTeam(teamLetter);
    setIsPlayerModalOpen(true);
  };

  // Nova funkcija koju poziva Modal kad klikneš "Spremi"
  const handleSavePlayer = async (playerData) => {
    const { name, no, teamLetter } = playerData;
    const newPlayer = { name, no, goals: 0 };
    const isA = teamLetter === "A";

    const teamId = isA ? activeMatch.team_a_id : activeMatch.team_b_id;
    const currentRoster = isA ? activeMatch.rostera : activeMatch.rosterb;
    const updatedRoster = [...currentRoster, newPlayer];

    setActiveMatch({
      ...activeMatch,
      [isA ? "rostera" : "rosterb"]: updatedRoster,
    });

    setIsPlayerModalOpen(false);

    // Spremi u bazu
    await supabase
      .from("teams")
      .update({ players: updatedRoster })
      .eq("id", teamId);
    await syncWithDB({ [isA ? "rostera" : "rosterb"]: updatedRoster });
  };

  // Dodavanje gola, kartona ili autogola
  const addEvent = async (eventData) => {
    let { scorea, scoreb, events } = activeMatch;
    let updatedEvents = [...events];

    // Ako uređujemo postojeći, prvo poništimo stari utjecaj na rezultat
    if (editingEvent) {
      const old = editingEvent;
      if (old.type === "goal" || old.type === "penalty")
        old.team === "A" ? scorea-- : scoreb--;
      if (old.type === "og") old.team === "A" ? scoreb-- : scorea--;
      updatedEvents = updatedEvents.filter((e) => e.id !== old.id);
    }
    const isA = eventData.team === "A";
    // Dodaj novi/uređeni događaj
    // U AdminStartMatch.js unutar addEvent funkcije
    const eventToAdd = {
      ...eventData,
      id: editingEvent?.id || Date.now(),
      // Spremanje imena i ID-a direktno u JSON događaja
      teamName: isA ? activeMatch.display_name_a : activeMatch.display_name_b,
      teamId: isA ? activeMatch.team_a_id : activeMatch.team_b_id,
    };

    updatedEvents.push(eventToAdd);

    // Primjeni novi utjecaj na rezultat
    if (eventToAdd.type === "goal" || eventToAdd.type === "penalty")
      eventToAdd.team === "A" ? scorea++ : scoreb++;
    if (eventToAdd.type === "og") eventToAdd.team === "A" ? scoreb++ : scorea++;

    const updated = { ...activeMatch, scorea, scoreb, events: updatedEvents };
    setActiveMatch(updated);
    setIsEventModalOpen(false);
    setEditingEvent(null);
    await syncWithDB({ scorea, scoreb, events: updatedEvents });
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm("Sigurno želiš obrisati ovaj događaj?")) return;
    let { scorea, scoreb, events } = activeMatch;
    const e = events.find((ev) => ev.id === eventId);

    if (e.type === "goal" || e.type === "penalty")
      e.team === "A" ? scorea-- : scoreb--;
    if (e.type === "og") e.team === "A" ? scoreb-- : scorea--;

    const updatedEvents = events.filter((ev) => ev.id !== eventId);
    const updated = { ...activeMatch, scorea, scoreb, events: updatedEvents };

    setActiveMatch(updated);
    setIsEventModalOpen(false);
    setEditingEvent(null);
    await syncWithDB({ scorea, scoreb, events: updatedEvents });
  };

  const finishMatch = async () => {
    setIsConfirmModalOpen(false);
    const { id, scorea, scoreb, events, rostera, rosterb, bracket_slot_id } =
      activeMatch;

    // 1. Pošalji update u bazu za trenutnu utakmicu
    const { error } = await supabase
      .from("matches")
      .update({
        scorea,
        scoreb,
        events,
        is_completed: true,
        is_live: false,
        rostera,
        rosterb,
      })
      .eq("id", id);

    if (error) {
      console.error("Greška pri završetku:", error);
      alert("Došlo je do greške!");
      return;
    }

    // --- AUTOMATIZACIJA ZA FINALE I 3. MJESTO ---
    // Provjeravamo je li završena utakmica bila polufinale (Slot 13 ili 14)
    if (bracket_slot_id === 13 || bracket_slot_id === 14) {
      const winnerId =
        scorea > scoreb ? activeMatch.team_a_id : activeMatch.team_b_id;
      const loserId =
        scorea > scoreb ? activeMatch.team_b_id : activeMatch.team_a_id;

      if (bracket_slot_id === 13) {
        // Pobjednik ide u finale (Slot 15, Team A), Gubitnik u 3. mjesto (Slot 16, Team A)
        await supabase
          .from("matches")
          .update({ team_a_id: winnerId })
          .eq("bracket_slot_id", 15);
        await supabase
          .from("matches")
          .update({ team_a_id: loserId })
          .eq("bracket_slot_id", 16);
      } else {
        // Pobjednik ide u finale (Slot 15, Team B), Gubitnik u 3. mjesto (Slot 16, Team B)
        await supabase
          .from("matches")
          .update({ team_b_id: winnerId })
          .eq("bracket_slot_id", 15);
        await supabase
          .from("matches")
          .update({ team_b_id: loserId })
          .eq("bracket_slot_id", 16);
      }
    }
    // --------------------------------------------

    alert("Utakmica je završena i spremljena!");
    setScheduledMatches((prev) => prev.filter((m) => m.id !== id));
    setActiveMatch(null);
    loadMatches();
  };

  if (loading) return <LoaderBox text="Učitavanje podataka..." />;

  // --- LISTA UTAKMICA ---
  // --- LISTA UTAKMICA ---
  if (!activeMatch) {
    return (
      <div className="admin-match-list">
        <h1>pokretanje utakmice</h1>
        {scheduledMatches.filter((m) => m.is_completed === false).length ===
        0 ? (
          <div
            className="empty-info"
            style={{
              textAlign: "center",
              padding: "30px",
              color: "#aaa",
              fontStyle: "italic",
            }}
          >
            Trenutno nema utakmica kojima je dodijeljen raspored.
          </div>
        ) : (
          scheduledMatches
            .filter((m) => m.is_completed === false)
            .sort((a, b) => {
              const hasTimeA = a.match_day && a.match_time;
              const hasTimeB = b.match_day && b.match_time;

              // Ako jedna ima termin, a druga nema, ona s terminom ide gore (ispred)
              if (hasTimeA && !hasTimeB) return -1;
              if (!hasTimeA && hasTimeB) return 1;

              // Ako NIJEDNA nema termin, sortiraj ih po bracket_slot_id na dnu (npr. polufinale pa finale)
              if (!hasTimeA && !hasTimeB) {
                return (a.bracket_slot_id || 0) - (b.bracket_slot_id || 0);
              }

              // 2. Ako OBJE imaju termin, sortiraj ih standardno po danima
              const DANI_REDOSLIJED = [
                "Uto 23.6.",
                "Sri 24.6.",
                "Čet 25.6.",
                "Pet 26.6.",
                "Sub 27.6.",
              ];
              const dayA = DANI_REDOSLIJED.indexOf(a.match_day);
              const dayB = DANI_REDOSLIJED.indexOf(b.match_day);

              if (dayA === dayB) {
                return a.match_time.localeCompare(b.match_time);
              }

              return dayA - dayB;
            })
            .map((m) => (
              <div
                key={m.id}
                className={`admin-match-item ${m.is_live ? "live-border" : ""}`}
              >
                <div className="match-info">
                  <span className="teams">
                    {m.display_name_a} vs {m.display_name_b}
                  </span>
                  <span className={`time ${m.is_live ? "live-text" : ""}`}>
                    {m.is_live
                      ? "● PRIJENOS U TIJEKU"
                      : `${m.match_time}h | ${m.match_day}`}
                  </span>
                </div>
                <button
                  className={`play-btn ${m.is_live ? "is-live-btn" : ""} ${anyMatchLive && !m.is_live ? "disabled-btn" : ""}`}
                  onClick={() => {
                    if (!m.is_live && anyMatchLive) {
                      alert(
                        "Već postoji aktivna utakmica! Završite trenutnu prije pokretanja nove.",
                      );
                      return;
                    }
                    setActiveMatch(m);
                  }}
                >
                  {m.is_live ? "LIVE" : "▶"}
                </button>
              </div>
            ))
        )}
      </div>
    );
  }

  // --- UPRAVLJANJE UTAKMICOM ---
  return (
    <div className="live-container admin-mode">
      <div className="live-card">
        <button
          className="back-btn-simple"
          onClick={() => setActiveMatch(null)}
        >
          ⬅ Nazad na listu
        </button>
        <div className="live-status-header">
          <div
            className={`live-badge ${activeMatch.is_live ? "live" : "prep"}`}
          >
            <span className="dot"></span>{" "}
            {activeMatch.is_live ? "UŽIVO" : "NA ČEKANJU"}
          </div>
        </div>
        <div className="scoreboard">
          <div
            className="team"
            onClick={() => {
              setModalType("A");
              setIsEventModalOpen(true);
            }}
          >
            <img className="club-shield" src={activeMatch.logo_a} alt="logo" />
            <p className="team-name">{activeMatch.display_name_a}</p>
            <span>Dodaj događaj</span>
          </div>
          <div className="score">
            {activeMatch.scorea} : {activeMatch.scoreb}
          </div>
          <div
            className="team"
            onClick={() => {
              setModalType("B");
              setIsEventModalOpen(true);
            }}
          >
            <img className="club-shield" src={activeMatch.logo_b} alt="logo" />
            <p className="team-name">{activeMatch.display_name_b}</p>
            <span>Dodaj događaj</span>
          </div>
        </div>
        <div className="half-switch">
          <button
            className={activeMatch.half === "1. poluvrijeme" ? "active" : ""}
            onClick={() => {
              setActiveMatch({ ...activeMatch, half: "1. poluvrijeme" });
              syncWithDB({ half: "1. poluvrijeme" });
            }}
          >
            1. Poluvrijeme
          </button>
          <button
            className={activeMatch.half === "2. poluvrijeme" ? "active" : ""}
            onClick={() => {
              setActiveMatch({ ...activeMatch, half: "2. poluvrijeme" });
              syncWithDB({ half: "2. poluvrijeme" });
            }}
          >
            2. Poluvrijeme
          </button>
        </div>

        <div className="admin-actions-grid">
          <button
            className={`main-status-btn ${activeMatch.is_live ? "stop" : "start"}`}
            disabled={!activeMatch.is_live && anyMatchLive} // Onemogući ako želi pokrenuti, a već postoji druga live
            onClick={() => {
              if (!activeMatch.is_live && anyMatchLive) {
                alert("Greška: Druga utakmica je već uživo.");
                return;
              }
              const s = !activeMatch.is_live;
              setActiveMatch({ ...activeMatch, is_live: s });
              syncWithDB({ is_live: s });

              // Lokalno ažuriraj listu da se odmah onemoguće ostali gumbi
              setScheduledMatches((prev) =>
                prev.map((m) =>
                  m.id === activeMatch.id ? { ...m, is_live: s } : m,
                ),
              );
            }}
            style={{
              opacity: !activeMatch.is_live && anyMatchLive ? 0.5 : 1,
              cursor:
                !activeMatch.is_live && anyMatchLive
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            {activeMatch.is_live ? "ZAUSTAVI LIVE" : "POKRENI LIVE"}
          </button>

          {activeMatch.is_live && (
            <button
              className="main-status-btn finish-btn"
              onClick={() => setIsConfirmModalOpen(true)}
            >
              ZAVRŠI UTAKMICU
            </button>
          )}
        </div>
        <div className="timeline">
          {activeMatch.events
            ?.slice()
            .sort((a, b) => parseInt(a.time) - parseInt(b.time))
            .map((e, i) => (
              <div
                key={i}
                className={`event-row ${e.team === "A" ? "left" : "right"}`}
              >
                <div className="event-content">
                  <button
                    className="edit-min-btn"
                    onClick={() => {
                      setEditingEvent(e);
                      setModalType(e.team);
                      setIsEventModalOpen(true);
                    }}
                  >
                    <i className="fi fi-rs-pencil"></i>
                  </button>
                  <span className="time">{e.time}'</span>
                  <EventIcon type={e.type} />
                  <span className="player">{e.player}</span>
                </div>
                <div className="center-line"></div>
              </div>
            ))}
        </div>
        {/* LISTA IGRAČA */}
        <div className="players-list-actions">
          <button
            onClick={() =>
              setShowRosterAdmin(showRosterAdmin === "A" ? null : "A")
            }
          >
            Igrači {activeMatch.display_name_a}
          </button>
          <button
            onClick={() =>
              setShowRosterAdmin(showRosterAdmin === "B" ? null : "B")
            }
          >
            Igrači {activeMatch.display_name_b}
          </button>
        </div>
        {showRosterAdmin && (
          <div className="admin-roster-box">
            <h4>
              Sastav:{" "}
              {showRosterAdmin === "A"
                ? activeMatch.display_name_a
                : activeMatch.display_name_b}
            </h4>
            {(showRosterAdmin === "A"
              ? activeMatch.rostera
              : activeMatch.rosterb
            ).map((p, i) => (
              <div key={i} className="player-row">
                <span className="number">{p.no}.</span>{" "}
                <span className="name">{p.name}</span>
              </div>
            ))}
            <button
              className="add-player-btn-small"
              onClick={() => addNewPlayer(showRosterAdmin)}
            >
              + DODAJ NOVOG IGRAČA
            </button>
          </div>
        )}
      </div>

      {isEventModalOpen && (
        <EventModal
          team={modalType}
          teamName={
            modalType === "A"
              ? activeMatch.display_name_a
              : activeMatch.display_name_b
          }
          roster={modalType === "A" ? activeMatch.rostera : activeMatch.rosterb}
          editingEvent={editingEvent}
          onClose={() => {
            setIsEventModalOpen(false);
            setEditingEvent(null);
          }}
          onAdd={addEvent}
          onDelete={deleteEvent}
        />
      )}

      {isPlayerModalOpen && (
        <PlayerModal
          teamLetter={playerModalTeam}
          teamName={
            playerModalTeam === "A"
              ? activeMatch.display_name_a
              : activeMatch.display_name_b
          }
          onClose={() => setIsPlayerModalOpen(false)}
          onSave={handleSavePlayer}
        />
      )}

      {isConfirmModalOpen && (
        <div className="modal-overlay">
          <div className="confirm-modal">
            <h3>Završi utakmicu?</h3>
            <p>
              Konačni rezultat:{" "}
              <b>
                {activeMatch.scorea}:{activeMatch.scoreb}
              </b>
            </p>
            <div className="confirm-btns">
              <button className="confirm-finish-btn" onClick={finishMatch}>
                POTVRDI
              </button>
              <button
                className="cancel-btn"
                onClick={() => setIsConfirmModalOpen(false)}
              >
                ODUSTANI
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EventIcon = ({ type }) => {
  switch (type) {
    case "goal":
      return (
        <div className="goal-icon" title="Gol">
          <i className="fi fi-br-football"></i>
        </div>
      );
    case "penalty":
      return (
        <div className="penalty-icon" title="Penal">
          <i className="fi fi-br-football"></i>
        </div>
      );
    case "penalty-miss":
      return (
        <div className="penalty-icon miss" title="Promašaj">
          <i className="fi fi-rs-cross-small"></i>
        </div>
      );
    case "yellow":
      return (
        <div className="two-min-out-icon" title="2 min">
          <p>2</p>
          <span>min</span>
        </div>
      );
    case "og":
      return (
        <div className="autogoal" title="Autogol">
          AG
        </div>
      );
    default:
      return <div className="c-icon c-default"></div>;
  }
};

const PlayerModal = ({ teamLetter, teamName, onClose, onSave }) => {
  const [playerName, setPlayerName] = useState("");
  const [playerNo, setPlayerNo] = useState("");

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="event-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Dodaj igrača: {teamName}</h3>
        <input
          type="text"
          placeholder="Ime i prezime"
          value={playerName}
          onChange={(e) => setPlayerName(e.target.value)}
        />
        <input
          type="number"
          placeholder="Broj dresa"
          value={playerNo}
          onChange={(e) => setPlayerNo(e.target.value)}
        />
        <div className="modal-btns-row">
          <button
            className="add-btn"
            onClick={() =>
              onSave({ name: playerName, no: playerNo, teamLetter })
            }
            disabled={!playerName || !playerNo}
          >
            SPREMI IGRAČA
          </button>
          <button className="cancel-btn-link" onClick={onClose}>
            Odustani
          </button>
        </div>
      </div>
    </div>
  );
};

const EventModal = ({
  team,
  teamName,
  roster,
  onClose,
  onAdd,
  editingEvent,
  onDelete,
}) => {
  const [min, setMin] = useState(editingEvent ? editingEvent.time : "");
  const [player, setPlayer] = useState(editingEvent ? editingEvent.player : "");
  const [type, setType] = useState(editingEvent ? editingEvent.type : "goal");

  // Definiramo tipove
  const eventTypes = [
    { id: "goal", label: "GOL" },
    { id: "penalty", label: "PENAL" },
    { id: "yellow", label: "2 MIN" },
    { id: "og", label: "AUTOGOL" },
    { id: "penalty-miss", label: "PROMAŠAJ" },
    { id: null, label: "" },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="event-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{editingEvent ? "Uredi događaj" : `${teamName} - Događaj`}</h3>

        <input
          type="number"
          placeholder="Minuta"
          value={min}
          onChange={(e) => setMin(e.target.value)}
        />

        <select value={player} onChange={(e) => setPlayer(e.target.value)}>
          <option value="">Odaberi igrača</option>
          {roster.map((p, i) => (
            <option key={i} value={p.name}>
              {p.no}. {p.name}
            </option>
          ))}
        </select>

        {/* OVDJE JE BIO PROBLEM - koristimo EventIcon komponentu */}
        <div className="event-type-grid">
          {eventTypes.map((t) => (
            <button
              key={t.id || "empty"}
              type="button"
              className={`type-btn ${type === t.id ? "active" : ""}`}
              onClick={() => t.id && setType(t.id)}
              disabled={!t.id}
            >
              {t.id ? (
                <EventIcon type={t.id} />
              ) : (
                <div style={{ height: "20px" }} />
              )}
              <span>{t.label}</span>
            </button>
          ))}
        </div>

        <div className="modal-btns-row">
          <button
            className="add-btn"
            onClick={() => onAdd({ team, player, time: min, type })}
          >
            {editingEvent ? "SPREMI" : "DODAJ"}
          </button>
          {editingEvent && (
            <button
              className="delete-btn"
              onClick={() => onDelete(editingEvent.id)}
            >
              OBRIŠI
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminStartMatch;
